"use client"

import type React from "react"

import { useState } from "react"
import { Button } from "#/components/ui/button"
import { Card } from "#/components/ui/card"
import { Label } from "#/components/ui/label"
import { Textarea } from "#/components/ui/textarea"
import { authClient } from "#/lib/auth/client"

const backgroundColors = [
  { name: "Dark Blue", value: "#1a1a2e", textColor: "#ffffff" },
  { name: "Deep Purple", value: "#16213e", textColor: "#ffffff" },
  { name: "Forest Green", value: "#0f3460", textColor: "#ffffff" },
  { name: "Burgundy", value: "#533a71", textColor: "#ffffff" },
  { name: "Charcoal", value: "#2c2c2c", textColor: "#ffffff" },
  { name: "Teal", value: "#1a5f7a", textColor: "#ffffff" },
]

interface TextPostFormProps {
  onPostCreated: () => void
}

export function TextPostForm({ onPostCreated }: TextPostFormProps) {
  const [content, setContent] = useState("")
  const [selectedBg, setSelectedBg] = useState(backgroundColors[0])
  const [isPosting, setIsPosting] = useState(false)
  const { session } = authClient.useSession()

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!content.trim() || !session) return

    setIsPosting(true)

    // Mock posting delay
    await new Promise(resolve => setTimeout(resolve, 1000))

    // In a real app, you would create the text post
    console.log("Creating text post:", {
      content,
      backgroundColor: selectedBg.value,
      textColor: selectedBg.textColor,
    })

    setIsPosting(false)
    onPostCreated()
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="space-y-4">
        <Label>Background Color</Label>
        <div className="grid grid-cols-3 gap-3">
          {backgroundColors.map(bg => (
            <button
              key={bg.value}
              type="button"
              className={`h-12 rounded-lg border-2 transition-all ${
                selectedBg.value === bg.value
                  ? "border-primary scale-105"
                  : "border-transparent"
              }`}
              style={{ backgroundColor: bg.value }}
              onClick={() => setSelectedBg(bg)}
            />
          ))}
        </div>
      </div>

      <div className="space-y-4">
        <Label htmlFor="content">Your thoughts</Label>
        <Card
          className="p-6 min-h-[200px] flex items-center justify-center"
          style={{
            backgroundColor: selectedBg.value,
            color: selectedBg.textColor,
          }}
        >
          <Textarea
            id="content"
            placeholder="Share what's on your mind..."
            value={content}
            onChange={e => setContent(e.target.value)}
            className="border-none bg-transparent text-center text-lg resize-none focus:ring-0 placeholder:text-current placeholder:opacity-60"
            style={{ color: selectedBg.textColor }}
            rows={6}
            maxLength={280}
          />
        </Card>
        <div className="text-right text-sm text-muted-foreground">
          {content.length}/280
        </div>
      </div>

      <Button
        type="submit"
        className="w-full"
        disabled={!content.trim() || content.length > 280 || isPosting}
      >
        {isPosting ? "Posting..." : "Share Thoughts"}
      </Button>
    </form>
  )
}
